import React from 'react'
import { Dialog } from '@mui/material'
import { useDataProvider, useNotify } from 'react-admin'
import { useSubmitLoader } from '@src/logic/contexts/utils/SubmitContext.tsx'
import { useModal } from '@src/logic/contexts/Modal/ModalContext'
import { useDocumentTitle } from '@src/hooks/useDocumentTitle'
import { AccountF } from '@src/logic/contexts/Accounts/AccountsFrontend.type'
import styles from './AccountEditForm.module.scss'
import { AccountEditForm, AccountEditFormType, parseAccountEditFormToBackend } from './AccountEditForm'
import { cn } from '@src/utils/cn'

type Props = {
  account: AccountF
  onUpdated?: () => void        
}

export const AccountEdit: React.FC<Props> = ({ account, onUpdated }) => {
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const { isFormSubmitting, setIsFormSubmitting } = useSubmitLoader()
  const { hideModal } = useModal()

  const accName = account?.accountName ?? (account as any)?.name ?? 'Account'
  useDocumentTitle(`Edit ${accName}`)

  const clearAndClose = () => hideModal()

  const handleDialogClose = (_: unknown, reason?: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick') return
    clearAndClose()
  }

  const onSubmit = async (formData: AccountEditFormType) => {
    if (isFormSubmitting) return
    setIsFormSubmitting(true)
    try {
      await dataProvider.update('Account', {
        
        id: (account as any)?.id ?? formData.id, 
        data: {
          action: 'update_account_info',
          data: parseAccountEditFormToBackend(formData),
        },
        previousData: account ?? null,
      })
      notify('Account updated successfully', { type: 'success' })
      onUpdated?.()
      clearAndClose()
    } catch (error: any) {
      const errorMessage = error?.message || 'Something went wrong, Please try again after sometime'
      notify(errorMessage, { type: 'error' })
    } finally {
      setIsFormSubmitting(false)
    }
  }

  return (
    <Dialog
      open
      onClose={handleDialogClose}
      role='dialog'
      aria-labelledby='edit-account-dialog-title'
      maxWidth='md'
      fullWidth
    >
      <div className={styles.dialogHeader}>
        <h3 id='edit-account-dialog-title'>Edit {accName}</h3>
      </div>

      <div data-page='edit-account-modal' className={cn(styles.dialogContent)}>
        <AccountEditForm
          account={account}
          onSubmit={onSubmit}
          onCancel={clearAndClose}
          isSubmitting={isFormSubmitting}
        />
      </div>
    </Dialog>
  )
}
